import { useState } from 'react';
import { SegmentedControl, Select } from '@/shared/components';
import type { PropertyGroupResponse, PropertyPortalBrandingResponse } from '@/shared/api';
import { GuestPortalBrand } from '@/features/guest/GuestPortalBrand';
import { GuestPhoneFrame } from '@/features/guest/preview/GuestPhoneFrame';
import { PortalStayPreview } from './PortalStayPreview';
import { usePortalPreviewData } from './usePortalPreviewData';

interface Props {
  group: PropertyGroupResponse;
  selectedId: string;
  branding: PropertyPortalBrandingResponse;
  logoUrl: string | null;
  token?: string;
  /** Anfahrt, Parken und Apartment-Daten liest nur, wer sie auch öffnen darf. */
  canReadSources: boolean;
  onSelectedIdChange: (value: string) => void;
}

/**
 * Das Portal eines Apartments, wie der Gast es am Anreisetag sieht: dieselben Bausteine, die
 * veröffentlichte Marke, ein Beispielaufenthalt statt einer Buchung. Codes bleiben verdeckt.
 */
export function PortalPreview(props: Props) {
  const { group, branding } = props;
  const [locale, setLocale] = useState<'de' | 'en'>('de');
  const apartment =
    group.apartments.find((item) => item.id === props.selectedId) ?? group.apartments[0];
  const data = usePortalPreviewData(group, apartment?.id, locale, props.canReadSources);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.08em]">
          Vorschau
        </span>
        <SegmentedControl
          size="touch"
          aria-label="Sprache der Vorschau"
          value={locale}
          onChange={setLocale}
          options={[
            { value: 'de', label: 'DE' },
            { value: 'en', label: 'EN' },
          ]}
        />
      </div>
      <label className="sr-only" htmlFor="portal-preview-apartment">
        Apartment für Vorschau
      </label>
      <Select
        id="portal-preview-apartment"
        value={apartment?.id ?? ''}
        disabled={group.apartments.length === 0}
        onChange={(event) => props.onSelectedIdChange(event.target.value)}
      >
        {group.apartments.length === 0 && <option value="">Kein Apartment verfügbar</option>}
        {group.apartments.map((item) => (
          <option key={item.id} value={item.id}>
            Vorschau für {item.name}
          </option>
        ))}
      </Select>
      {data.failed.length > 0 && (
        <p role="status" className="text-sealwax text-xs">
          Nicht geladen: {data.failed.join(', ')} – die Vorschau ist hier unvollständig.
        </p>
      )}
      <GuestPhoneFrame
        label="Gastportal-Vorschau"
        language={locale}
        brand={
          <GuestPortalBrand
            portalBrandName={branding.brandName}
            portalLogoUrl={props.logoUrl}
            token={props.token}
            fallbackLabel="StayOn"
          />
        }
      >
        <p className="text-fg-dim text-xs">
          <span className="text-fg font-semibold">{apartment?.name ?? 'Apartment'}</span> ·
          Beispielaufenthalt, ohne echte Gastdaten
        </p>
        <PortalStayPreview
          group={group}
          apartment={data.apartment}
          steps={data.steps}
          alternatives={data.alternatives}
          parking={data.parking}
          parkingFailed={data.parkingFailed}
          locale={locale}
        />
      </GuestPhoneFrame>
      <p className="text-ink-soft text-xs">
        Dieselben Bausteine wie das echte Portal. Codes und Gastdaten werden in der Vorschau nie
        aufgelöst.
      </p>
    </div>
  );
}
